import { truncateDecimalString } from '@/util/truncateDecimalString'

export interface CurrencyOverrides {
    display_position?: 'before' | 'after'
    decimal_separator?: string
    thousands_separator?: string
    decimal_places?: number
}

export interface CurrencyConfig {
    code: string // e.g. "EUR"
    locale: string // e.g. "nl-NL"
    display?: 'symbol' | 'code' | 'name'
    overrides?: CurrencyOverrides
}

export function normalizeLocaleCode(locale?: string): string | undefined {
    if (!locale) return undefined
    return locale.replace('_', '-')
}

export function getCurrencyForLocale(locale?: string): string | undefined {
    if (!locale) return undefined

    const normalized = normalizeLocaleCode(locale)
    if (!normalized) return undefined

    const region = normalized.split('-')[1]?.toUpperCase()
    const regionCurrencyMap: Record<string, string> = {
        NL: 'EUR',
        BE: 'EUR',
        DE: 'EUR',
        FR: 'EUR',
        ES: 'EUR',
        IT: 'EUR',
        PT: 'EUR',
        IE: 'EUR',
        AT: 'EUR',
        FI: 'EUR',
        LU: 'EUR',
        US: 'USD',
        GB: 'GBP',
        CH: 'CHF',
        NO: 'NOK',
        SE: 'SEK',
        DK: 'DKK',
        PL: 'PLN',
        CZ: 'CZK',
    }

    if (region && regionCurrencyMap[region]) return regionCurrencyMap[region]

    const language = normalized.split('-')[0]?.toLowerCase()
    const languageCurrencyMap: Record<string, string> = {
        nl: 'EUR',
        de: 'EUR',
        fr: 'EUR',
        es: 'EUR',
        it: 'EUR',
        pt: 'EUR',
        en: 'USD',
    }

    return language ? languageCurrencyMap[language] : undefined
}

function getDisplayDecimalPlaces(
    currencyConfig?: CurrencyConfig | null
): number {
    const v = currencyConfig?.overrides?.decimal_places
    return typeof v === 'number' ? v : 2
}

/**
 * Formats a numeric value for display using the provided currency config.
 * Mirrors the logic used in `DecimalField.tsx` (Intl base + overrides).
 */
export function formatWithCurrencyConfig(
    value: string | number,
    currencyConfig: CurrencyConfig,
    fallbackLocale = 'nl-NL'
): string {
    const normalized = String(value).trim().replace(',', '.')
    const n = Number(normalized)
    if (!Number.isFinite(n)) return String(value)

    const overrides = currencyConfig.overrides ?? {}
    const decimalPlaces = getDisplayDecimalPlaces(currencyConfig)
    const truncatedN = Number(truncateDecimalString(normalized, decimalPlaces))
    const locale = normalizeLocaleCode(currencyConfig.locale) ?? fallbackLocale
    const currencyCode = currencyConfig.code
    const currencyDisplay =
        currencyConfig.display === 'code'
            ? 'code'
            : currencyConfig.display === 'name'
            ? 'name'
            : 'narrowSymbol'

    const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        currencyDisplay,
    }).format(truncatedN)

    let result = formatted

    if (overrides.decimal_separator || overrides.thousands_separator) {
        const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89)
        const intlDecimal =
            parts.find((p) => p.type === 'decimal')?.value ?? '.'
        const intlGroup = parts.find((p) => p.type === 'group')?.value ?? ','

        if (overrides.thousands_separator !== undefined) {
            result = result.split(intlGroup).join('\x00')
        }
        if (overrides.decimal_separator !== undefined) {
            result = result.split(intlDecimal).join(overrides.decimal_separator)
        }
        if (overrides.thousands_separator !== undefined) {
            result = result.split('\x00').join(overrides.thousands_separator)
        }
    }

    if (overrides.display_position) {
        const currencyParts = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay,
        }).formatToParts(truncatedN)

        const currencyPart =
            currencyParts.find((p) => p.type === 'currency')?.value ??
            currencyCode

        const withoutCurrency = result.replace(currencyPart, '').trim()

        result =
            overrides.display_position === 'after'
                ? `${withoutCurrency}\u00A0${currencyPart}`
                : `${currencyPart}\u00A0${withoutCurrency}`
    }

    return result
}

export function isCurrencyConfig(value: unknown): value is CurrencyConfig {
    return Boolean(value && typeof value === 'object' && 'code' in value)
}
